// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import type { To } from 'react-router-dom';
import { disbatch } from 'redux-act';
import { v4 } from 'uuid';

import { serializeQuery } from '@gandi/app-runner';
import { selectPricingInfos } from '@gandi/billing';
import {
  addCartProduct,
  getCart,
  selectCartIsWorking,
  selectCartUUID,
  updateProduct,
  type ProductPayload,
} from '@gandi/cart';
import type { ApplicationSummary } from '@gandi/redux-resource-simplehosting';
import { useTranslator } from '@gandi/translate';
import { Base, Spinner } from '@gandi/ui';

import { useAppSelector, usePrevious, useNotifications } from 'src/hooks';
import { useRequireAuthNavigate } from 'src/hooks/useRequireAuthNavigate';
import { stopSSE } from 'src/modules/app/sse';

import { change as setFilter } from '../DomainCreate/modules/actions/filters';
import { submitSearch } from '../DomainCreate/modules/actions/suggestions';
import type { ExtendedSuggestion } from '../DomainCreate/types';
import type { SimplehostingPriceSummary } from '../modules/simplehosting/prices';
import type { Dispatch, GetState } from '../types';

import { ProductContext } from './ProductContext';
import { Domains } from './components/Domains';
import Offers from './components/Offers';
import OffersIntro from './components/OffersIntro';
import { useHostingApplications } from './modules/hooks';
import {
  hasEssentialOffer,
  usePlanPrices,
  useSimplehostingPrices,
} from './modules/prices';
import { selectCartHasHostingProduct } from './modules/selectors';

import styles from './WebHosting.scss';

export type Params = {
  productId: string,
};

const RELOAD_EVENT = 'reload';
const BACK_EVENT = 'back_forward';

const EMPTY_ARRAY: Array<SimplehostingPriceSummary> = [];

const useGetLabel = () => {
  const { __ } = useTranslator();

  return (
    applicationName: string | void = undefined,
    fqdn: string | void = undefined,
  ): string | void => {
    if (fqdn) {
      return applicationName
        ? __('Website Pack %(applicationName)s', {
            applicationName,
          })
        : __('Web Hosting Pack');
    }

    return applicationName
      ? __('Web Hosting %(applicationName)s', { applicationName })
      : __('Web Hosting');
  };
};

type PayloadOffer = {
  ...Partial<SimplehostingPriceSummary>,
  product: {
    ...Partial<SimplehostingPriceSummary['product']>,
    name: SimplehostingPriceSummary['product']['name'],
  },
  min_duration: SimplehostingPriceSummary['min_duration'],
};

export const useGetHostingPayload = (): ({
  getHostingPayload: ({
    application?: ApplicationSummary,
    chosenOffer: SimplehostingPriceSummary | PayloadOffer,
    packId: string,
    fqdn?: string,
  }) => ProductPayload,
}) => {
  const getLabel = useGetLabel();

  return {
    getHostingPayload: ({
      application,
      chosenOffer,
      packId,
      fqdn,
    }): ProductPayload => {
      return {
        data_disk_additional_size: 0,
        // both yearly and monthly are using `month` as `duration_unit`
        // then it is more logic to use the `min_duration` to make difference between both
        duration: chosenOffer.min_duration,
        // XXX disabled for now, waiting for API to handle 'month'
        // duration_unit: chosenOffer.duration_unit[0], // should always be 'month' to have consistent catalog
        label: getLabel(application?.name_label, fqdn),
        product_name: 'webhosting',
        size: chosenOffer.product.name,
        trial: false,
        application_hint: application?.name,
        extra: {
          application: application?.name,
          packId,
        },
      };
    },
  };
};

function scrollToDomainSearch(
  ref: null,
  navigate: ((to: To, options?: { replace?: boolean, state?: any }) => void) &
    ((delta: number) => void),
  hash?: string,
  search?: string,
) {
  if (hash !== '#choose-a-domain') {
    navigate(
      {
        hash: '#choose-a-domain',
        search: serializeQuery({
          search,
        }),
      },
      {
        replace: true,
      },
    );
  }

  ref?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest',
  });
}

export default function WebHosting(): React.MixedElement {
  const navigate = useNavigate();
  const params = useParams<Params>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // productId from www
  const productId = Number.isNaN(Number(params.productId))
    ? undefined
    : Number(params.productId);

  const [packId, setpackId] = React.useState(v4() as string);
  const dispatch = useDispatch();
  const { getHostingPayload } = useGetHostingPayload();
  const { notifyMaybeApiError } = useNotifications();
  const getLabel = useGetLabel();
  const cartIsWorking = useAppSelector(selectCartIsWorking);

  const size = searchParams.get('size') || undefined;
  const type = searchParams.get('type') || undefined;
  const plan = 'yearly';
  const cartId = useAppSelector(selectCartUUID);
  const chooseDomainRef = React.useRef(null);

  const navigateToCartOverview = useRequireAuthNavigate(`/cart/${cartId}`);

  const { currency } = useAppSelector(selectPricingInfos);

  const { availableApplications, isFetching: isFetchingApplications } =
    useHostingApplications();

  const {
    isFetching: instancePricesFetching,
    isLoaded: instancesPricesLoaded,
  } = useSimplehostingPrices('create');

  const application = availableApplications.find(app => app.name === type);

  const planPrices = usePlanPrices(plan, application);

  const productContextValue = React.useMemo(() => ({ productId }), [productId]);

  const product = useAppSelector(state =>
    selectCartHasHostingProduct(state, {
      productId,
      application: application?.name,
      size,
      packId,
    }),
  );

  const prevProduct = usePrevious(product);

  React.useEffect(() => {
    if (productId !== undefined && !product) {
      navigate({
        pathname: `/simplehosting/create/${cartId}`,
        // TO_CONFIRM: it seems we need to keep the querystring
        search: searchParams.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, productId, product, navigate]); // we  don't need to navigate when querystring change

  React.useEffect(() => {
    if (performance.getEntriesByType('navigation')) {
      const p = performance.getEntriesByType('navigation')[0];

      // when we press the back button from checkout, or reload the page, we remove all url params and change packId to restart the process
      // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming

      // $FlowFixMe[prop-missing] - type is not missing
      if (p.type === RELOAD_EVENT || p.type === BACK_EVENT) {
        navigate(
          {
            hash: '',
          },
          { replace: true },
        );
        setpackId(v4());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const currentRef = chooseDomainRef.current;

    if (!prevProduct && product && currentRef !== null) {
      scrollToDomainSearch(currentRef, navigate, location?.hash);
    }

    if (location?.hash === '#choose-a-domain' && prevProduct && !product) {
      navigate(
        {
          hash: '',
        },
        { replace: true },
      );
    }
    // XXX: do we need to keep `application` in the list?
  }, [product, prevProduct, application, location?.hash, navigate]);

  React.useEffect(() => {
    async function addpackIdToPack() {
      try {
        await dispatch(
          updateProduct({
            // $FlowFixMe[incompatible-use]
            productId: product.id,
            payload: {
              ...product?.payload,
              // $FlowFixMe[incompatible-call] - we know it's not SHSExtra | CloudExtra
              extra: {
                ...product?.payload.extra,
                application: application?.name,
                packId,
              },
            },
          }),
        );
        return dispatch(getCart());
      } catch (e) {
        dispatch(notifyMaybeApiError(e));
        return Promise.reject();
      }
    }

    if (product && !product.payload?.extra?.packId && !cartIsWorking) {
      addpackIdToPack();
    }
  }, [
    product,
    application,
    dispatch,
    cartIsWorking,
    notifyMaybeApiError,
    packId,
    location?.hash,
  ]);

  const handleChooseDomain = async (
    suggestion: ExtendedSuggestion,
  ): Promise<void> => {
    const { tld, fqdn } = suggestion;
    if (product) {
      try {
        await dispatch(
          updateProduct({
            productId: product.id,
            payload: {
              ...product.payload,
              // $FlowFixMe[incompatible-call] - we know it's not SHSExtra | CloudExtra
              extra: {
                ...product.payload.extra,
                packId,
              },
              label: getLabel(application?.name_label, fqdn),
              domain: {
                fqdn,
                tld,
              },
            },
          }),
        );
        dispatch(getCart());

        navigateToCartOverview();
      } catch (e) {
        dispatch(notifyMaybeApiError(e));
      }
    }
  };

  const setHosting = async (
    offer: SimplehostingPriceSummary,
  ): Promise<void> => {
    const payload = getHostingPayload({
      application,
      chosenOffer: offer,
      packId,
    });
    const isEssential = payload.size === 'essential';

    try {
      // in case user wants to change hosting size
      if (product) {
        await dispatch(
          updateProduct({
            payload,
            productId: product.id,
          }),
        );
      } else {
        await dispatch(
          addCartProduct({
            productData: {
              payload,
              process: 'create',
              product_type: 'paas',
            },
            options: {
              headers: { 'X-Dry-Run': 0 },
            },
          }),
        );

        const freshCartId = dispatch((_: Dispatch, getState: GetState) =>
          selectCartUUID(getState()),
        );
        if (freshCartId) {
          navigate(
            {
              pathname: `/simplehosting/create/${freshCartId}/`,
              search: serializeQuery({
                type: application?.name,
              }),
              hash: '#choose-a-domain',
            },
            {
              replace: true,
            },
          );
        }
      }

      const filters = isEssential
        ? { tlds: 'fr,ca,uk,be,de,eu,nl,co.uk', filter_list: '' }
        : { tlds: '', filter_list: 'packhosting' };

      disbatch(
        dispatch,
        // $FlowFixMe[not-an-array]
        setFilter({ name: 'tlds', value: filters.tlds }),
        // $FlowFixMe[not-an-array]
        setFilter({ name: 'filter_list', value: filters.filter_list }),
      );

      const search = searchParams.get('search');

      if (search) {
        const currentRef = chooseDomainRef.current;
        scrollToDomainSearch(currentRef, navigate, location?.hash, search);

        stopSSE();
        dispatch(submitSearch(search));
      }
    } catch (e) {
      dispatch(notifyMaybeApiError(e));
      return Promise.reject();
    }

    // $FlowFixMe[incompatible-call]
    return dispatch(getCart());
  };

  const isLoading =
    instancePricesFetching || isFetchingApplications || !instancesPricesLoaded;

  if (isLoading) {
    return <Spinner name="dots" centered />;
  }

  return (
    <ProductContext.Provider value={productContextValue}>
      <div className={styles.intro}>
        <OffersIntro application={application} />
      </div>
      <Base marginBottom="medium">
        <Offers
          application={application}
          availableSizes={planPrices || EMPTY_ARRAY}
          popularSize={hasEssentialOffer(planPrices) ? 'essential' : 'advanced'}
          currency={currency}
          onClick={setHosting}
          packId={packId}
        />
      </Base>
      {product && (
        <Base marginBottom="medium">
          <Domains
            childRef={chooseDomainRef}
            application={application}
            handleChooseDomain={handleChooseDomain}
          />
        </Base>
      )}
    </ProductContext.Provider>
  );
}
